import React, { useState, useEffect, useContext } from 'react';
import UserContext from '../../components/User_Context.js';
import * as menu from "../../assets/menus/menu.js"
import { Organizational_Chart } from "../../components/ui/organizational_chart.js";
import { D3Tree } from "../../components/ui/d3_tree.js";
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import waiting from "../../assets/images/waiting.svg";
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import * as utils from "../../components/utils.js";
import { useTranslation } from 'react-i18next';
import { SystemLabels } from '../_label/system/system_labels.js';


const Business_Units_Screen = (params) => {
    const { currentUser } = useContext(UserContext);
    const [data, setData] = useState(null);
    const [unitsTypeVoc, setUnitsTypeVoc] = useState(null);
    const [hospitalRegimenTypesVoc, setHospitalRegimenTypesVoc] = useState(null);
    const [showChart, setShowChart] = useState(true);
    const { t } = useTranslation();

    // Initialize i18n labels from centralized file
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', SystemLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', SystemLabels.en, true, true);

    const setBreadCrumb = (params) => {
        var path = [{
            link: "/",
            name: t('home', { ns: 'labels' })
        },
        {
            link: "/admin/organization/business_units",
            name: t('admin', { ns: 'labels' })
        },
        {
            link: "/admin/organization/business_units",
            name: t('organizationalChart', { ns: 'labels' })
        }
        ];
        breadcrumb.set(path);
    }


    const retrieveData = async () => {

        const unitsResult = (await api.sendMessage("admin/system/organization/get_business_units", {
            user: currentUser(),
        }));

        if (unitsResult.success) {

            const units = unitsResult.units ?? [];


            var unitsTypeVoc = (await api.sendMessage("admin/vocs/get", {
                user: currentUser(),
                body: {
                    voc_id: "business_unit_types"
                }
            })).items;

            // Only load hospital regimen types if profile is not AIPLAN
            var hospitalRegimenTypesVoc = [];
            if (process.env.REACT_APP_PROFILE !== 'AIPLAN') {
                hospitalRegimenTypesVoc = (await api.sendMessage("admin/vocs/get", {
                    user: currentUser(),
                    body: {
                        voc_id: "hospital_regimen_types"
                    }
                })).items;
            }

            setUnitsTypeVoc(unitsTypeVoc);
            setHospitalRegimenTypesVoc(hospitalRegimenTypesVoc);
            setData(units);


        } else {
            withReactContent(Swal).fire({
                title: t('error', { ns: 'labels' }),
                icon: "error",
                text: t(unitsResult.message.toLowerCase(), { ns: 'labels' }),
                showConfirmButton: true,
                allowOutsideClick: false
            }).then(async (result) => {
                if (result.isConfirmed) {
                    window.location("/");
                }
            });
        }

    }

    useEffect(() => {
        (async () => {
            setBreadCrumb();
            menu.write({ user: currentUser() });
            await retrieveData();
        })();
    }, []);

    const _toSave = () => {
        return data.filter(x => x.__toUpdate);
    }

    const _validate = () => {
        const errors = [];

        for (const d of data) {
            if (!d.Name) {
                var error = t('missingNameForBU', { ns: 'labels', index: data.findIndex(x => x === d) + 1 });
                errors.push(error);
            }

            if (!d.UnitTypeCode) {
                var error = t('missingTypeForBU', { ns: 'labels', name: d.Name });
                errors.push(error);
            }
        }

        return errors;
    }

    const _save = async () => {
        const errors = _validate();

        withReactContent(Swal).fire({
            title: t('saving', { ns: 'labels' }),
            icon: "info",
            text: t('savingNewData', { ns: 'labels' }),
            allowOutsideClick: false
        });

        if (errors.length > 0) {
            withReactContent(Swal).fire({
                title: t('error', { ns: 'labels' }),
                icon: "error",
                text: errors.join(','),
                showConfirmButton: true
            });
        }
        else {
            const dataToUpdate = _toSave();

            if (dataToUpdate.length == 0) {
                withReactContent(Swal).fire({
                    title: t('nothingToSave', { ns: 'labels' }),
                    icon: "info",
                    text: t('nothingToSaveMessage', { ns: 'labels' }),
                    showConfirmButton: true
                });
                return { success: false };
            }

            // Separate saved and deleted items
            const savedItems = dataToUpdate.filter(x => !x.Deleted);
            const deletedItems = dataToUpdate.filter(x => x.Deleted);
            const saveCount = savedItems.length;
            const deleteCount = deletedItems.length;

            const result = await api.sendMessage("admin/system/organization/save_business_units", {
                user: currentUser(),
                body: {
                    units: dataToUpdate
                }

            });

            if (result.success) {
                // Build appropriate success message
                let successMessage = '';
                
                if (saveCount > 0 && deleteCount > 0) {
                    successMessage = `${saveCount} ${t('savedAndDeletedUnitsMessage', { ns: 'labels' })} ${deleteCount} ${t('deletedMessage', { ns: 'labels' })}`;
                } else if (saveCount > 0) {
                    successMessage = `${saveCount} ${t('savedMessage', { ns: 'labels' })}`;
                } else if (deleteCount > 0) {
                    successMessage = `${deleteCount} ${t('deletedMessage', { ns: 'labels' })}`;
                }

                withReactContent(Swal).fire({
                    title: t('success', { ns: 'labels' }),
                    icon: "success",
                    text: successMessage,
                    showConfirmButton: true
                });

                for (const d of data) {
                    d.__toUpdate = false;
                    setData(utils.clone(data));
                }

                return result;
            }
            else {
                if (result.messages?.length > 0) {
                    withReactContent(Swal).fire({
                        title: t('error', { ns: 'labels' }),
                        icon: "error",
                        text: result.messages.join(','),
                        showConfirmButton: true
                    });

                    return result;
                } else {
                    withReactContent(Swal).fire({
                        title: t('error', { ns: 'labels' }),
                        icon: "error",
                        text: t(result.message.toLowerCase(), { ns: 'labels' }),
                        showConfirmButton: true
                    }).then(async (result) => {
                        if (result.isConfirmed) {
                            window.location.reload();
                        }
                    });
                }
            }
        }

    }

    const updateData = (newNodes) => {
        setData(utils.clone(newNodes));
    }

    return (
        <div className="d-flex flex-column" style={{ height: '100vh' }}>

            {/* ─────────────────────────────────────────────────────────────
            1) HEADER + TOOLBAR (non scorre mai)
            ───────────────────────────────────────────────────────────── */}
            <header className="bg-white shadow-sm">
                <div className="container-fluid d-flex justify-content-between align-items-center py-2">
                    {/* → breadcrumb */}
                    <ol className="breadcrumb mb-0"></ol>

                    {/* → icone save / delete */}
                    <div className="d-flex gap-3">
                        <a
                            className="text-primary fs-13"
                            onClick={(e) => {
                                _save();
                            }}
                            role="button"
                            title={t('saveThisData', { ns: 'labels' })}
                        >
                            <i className="fa-solid fa-floppy-disk fa-2x"></i>
                        </a>

                        <a
                            className="text-primary fs-13"
                            onClick={(e) => {
                                setShowChart(!showChart);
                            }}
                            role="button"
                            title={showChart ? t('switchToTreeView', { ns: 'labels' }) : t('switchToListView', { ns: 'labels' })}
                        >
                            <i className={`fa-solid fa-${showChart ? 'diagram-project' : 'list'} fa-2x`}></i>
                        </a>
                    </div>
                </div>
            </header>

            {/* ─────────────────────────────────────────────────────────────
            2) ZONA SCROLLABILE
            ───────────────────────────────────────────────────────────── */}
            <main className="flex-grow-1 overflow-auto">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-xl-12">
                            <div className="card">
                                <div className="card-body p-0">
                                    <div className="tab-content" id="tabs-content" style={{ padding: 15 }}>
                                        {data ? (
                                            showChart ? (
                                                <div id="organizationalChartContainer">
                                                    <Organizational_Chart
                                                        nodes={data}
                                                        onChange={(e) => {
                                                            updateData(e)
                                                        }}
                                                        onSaveRequest={async ({ next }) => {
                                                            const _result = await _save();
                                                            if (_result.success) {
                                                                next();
                                                            }
                                                        }}
                                                        container={window.$("#organizationalChartContainer")}
                                                        unitsTypeVoc={unitsTypeVoc}
                                                        hospitalRegimenTypesVoc={hospitalRegimenTypesVoc}
                                                    />
                                                </div>
                                            ) : (
                                                <div>
                                                    <D3Tree data={data} />
                                                </div>
                                            )
                                        ) : <div className="text-center"><img src={waiting} /></div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    )
}

export { Business_Units_Screen }
